/**
 * The whole till screen, small, with a spotlight on one part of it.
 *
 * The close-up beside it shows *what* to press; this shows *where* it is. A
 * cashier who has never used a till does not know that the money buttons live
 * under the basket on the right, or that the coloured chip at the top is the
 * signal — so every step that happens on the screen shows the screen first,
 * dims everything else, and rings the part the step is about.
 *
 * The layouts follow the real pages: the till (SalesPage), the sign-in card
 * (LoginPage), and the dialogs that open over the till. They are sketches, not
 * screenshots, so they never go stale in a way that matters: a region moves
 * only when the real page moves it.
 */

import { Banknote, CreditCard, KeyRound, Pause, Play, Wifi, WifiOff } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/cn";

export type MapScreen =
  | "login"
  | "till"
  | "payment"
  | "receipt"
  | "refund"
  | "held"
  | "open-shift"
  | "cash-up"
  | "shift-closed"
  | "home";

export type Where = TillWhere | OfficeWhere | FormWhere;

/** A till screen, drawn from its own layout below. */
export interface TillWhere {
  screen: MapScreen;
  /** The region to light up. Names are per screen; see each layout below. */
  focus: string;
  /** Draw the status chip as offline. */
  offline?: boolean;
}

export const NAV = [
  "Dashboard",
  "Sales",
  "Customers",
  "Inventory",
  "Marketplace",
  "Suppliers",
  "Procurement",
  "Reports",
  "Expenses",
  "Billing",
  "Settings",
] as const;

export type Tone = "brand" | "money" | "plain" | "danger";

/**
 * A back-office page, described rather than drawn by hand: they all share one
 * shape — the menu down the left, a title, then some of tabs, buttons, figures,
 * filters and a table — so a page is a list of which of those it has.
 *
 * Focus names: `nav` (the page's menu item), `tab:<name>`, `action:<id>`,
 * `kpis`, `filter:<label>`, `rows`, `row-action`, `panel`.
 */
export interface OfficePage {
  nav: (typeof NAV)[number];
  title: string;
  tabs?: string[];
  tab?: string;
  actions?: { id: string; label: string; tone?: Tone }[];
  kpis?: number;
  filters?: string[];
  rows?: number;
  /** A button at the end of the first row, e.g. "Receive" or "Set PIN". */
  rowAction?: { label: string; tone?: Tone };
  /** A labelled block at the bottom, e.g. "Top low stock items". */
  panel?: string;
}

export interface OfficeWhere {
  screen: "office";
  page: OfficePage;
  focus: string;
}

/**
 * A form that opens over a page (or over the till): a title, its fields in
 * order, and its buttons. Focus names: a field's or button's `id`.
 */
export interface FormWhere {
  screen: "form";
  over: OfficePage | "till";
  title: string;
  fields: { id: string; label: string; value?: string; kind?: "field" | "toggle" | "pin" }[];
  buttons: { id: string; label: string; tone?: Tone }[];
  focus: string;
}

/** A box on the map, in percent of the screen. */
type Box = [left: number, top: number, width: number, height: number];

function place([left, top, width, height]: Box): CSSProperties {
  return { left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` };
}

/**
 * One part of the screen. The focused one throws a dark shadow over the whole
 * screen except itself — a spotlight — and gets the same gold ring as the
 * close-up, so the two pictures read as one.
 */
function R({
  id,
  focus,
  box,
  className,
  children,
}: {
  id: string;
  focus: string;
  box: Box;
  className?: string;
  children?: ReactNode;
}) {
  const lit = id === focus;
  return (
    <div
      style={place(box)}
      data-lit={lit || undefined}
      className={cn(
        "absolute overflow-hidden rounded-[4px]",
        lit &&
          "z-10 shadow-[0_0_0_999px_rgb(var(--ss-navy-950)/0.6)] outline outline-[3px] outline-offset-2 outline-gold-500",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Grey lines standing in for text. */
function Lines({ n, className }: { n: number; className?: string }) {
  return (
    <div className={cn("grid gap-[3px] p-[5%]", className)}>
      {Array.from({ length: n }, (_, i) => (
        <span
          key={i}
          className="h-[3px] rounded-full bg-ink-400/50"
          style={{ width: `${90 - i * 13}%` }}
        />
      ))}
    </div>
  );
}

function Tiny({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "grid h-full w-full place-items-center gap-0.5 whitespace-nowrap text-[clamp(6px,1.9cqw,10px)] font-semibold leading-none",
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── The till itself ─────────────────────────────────────────────────────────

function TillScreen({ focus, offline }: { focus: string; offline?: boolean }) {
  return (
    <>
      {/* Top bar: page title on the left; the chip and help on the right. */}
      <div className="absolute inset-x-0 top-0 h-[9%] border-b border-line bg-surface" />
      <span className="absolute left-[15%] top-[2.5%] h-[4%] w-[12%] rounded-sm bg-ink-900/70" />
      <R id="chip" focus={focus} box={[64, 1.5, 18, 6]} className="rounded-full">
        <Tiny
          className={cn(
            "flex rounded-full",
            offline ? "bg-warn-50 text-warn-700" : "bg-money-50 text-money-700",
          )}
        >
          <span className="inline-flex items-center gap-0.5">
            {offline ? <WifiOff className="size-[1.1em]" /> : <Wifi className="size-[1.1em]" />}
            {offline ? "Offline 3" : "Online"}
          </span>
        </Tiny>
      </R>
      <span className="absolute right-[3%] top-[2%] size-[5%] rounded-full bg-ink-400/40" />

      {/* The menu down the left. */}
      <div className="absolute bottom-0 left-0 top-0 w-[12%] bg-navy-900">
        <Lines n={6} className="mt-[90%] [&>span]:bg-white/30" />
      </div>

      {/* Shift bar, above the till. */}
      <R id="shiftbar" focus={focus} box={[14, 11, 84, 6]} className="bg-surface">
        <span className="flex h-full items-center justify-between px-[1.5%]">
          <span className="h-[30%] w-[35%] rounded-sm bg-ink-400/50" />
          <Tiny className="w-auto rounded-[3px] bg-brand-500 px-[1.5%] text-white">Cash up</Tiny>
        </span>
      </R>

      {/* Left: search, then the products, then recent sales. */}
      <span className="absolute left-[14%] top-[19%] h-[5%] w-[50%] rounded-[3px] border border-line bg-surface" />
      <R id="grid" focus={focus} box={[14, 26, 50, 44]} className="bg-canvas">
        <div className="grid h-full grid-cols-[repeat(4,1fr)] grid-rows-[repeat(2,1fr)] gap-[4%] p-[2%]">
          {[0, 40, 200, 120, 280, 160, 320, 80].map((hue) => (
            <span
              key={hue}
              className="flex flex-col overflow-hidden rounded-[3px] border border-line bg-surface"
            >
              <span className="flex-1" style={{ backgroundColor: `hsl(${hue} 70% 92%)` }} />
              <span className="h-[28%]" />
            </span>
          ))}
        </div>
      </R>
      <R id="recent" focus={focus} box={[14, 73, 50, 25]} className="border border-line bg-surface">
        <Tiny className="h-auto justify-items-start px-[4%] pt-[4%] text-ink-900">
          Recent sales
        </Tiny>
        <Lines n={3} />
      </R>

      {/* Right: the basket, and the money buttons under it. */}
      <div className="absolute left-[66%] top-[19%] h-[79%] w-[32%] rounded-[4px] border border-line bg-surface" />
      <R id="hold" focus={focus} box={[80, 20, 17, 6]}>
        <Tiny className="flex justify-end gap-[8%] text-ink-600">
          <span className="inline-flex items-center gap-0.5">
            <Pause className="size-[1.1em]" />
            Hold
          </span>
        </Tiny>
      </R>
      <R id="held" focus={focus} box={[67, 20, 13, 6]}>
        <Tiny className="flex text-ink-600">
          <span className="inline-flex items-center gap-0.5">
            <Play className="size-[1.1em]" />
            Held
          </span>
        </Tiny>
      </R>
      <R id="basket" focus={focus} box={[67, 27, 30, 36]} className="bg-surface">
        <Tiny className="h-auto justify-items-start px-[5%] pt-[5%] text-ink-900">
          Current sale
        </Tiny>
        <Lines n={4} />
        <span className="absolute inset-x-[5%] bottom-[8%] h-[6%] rounded-sm bg-ink-900/70" />
      </R>
      <R id="pay" focus={focus} box={[67, 66, 30, 11]}>
        <Tiny className="rounded-[3px] bg-money-500 text-white">Pay</Tiny>
      </R>
      <R id="cashcard" focus={focus} box={[67, 80, 30, 11]}>
        <span className="grid h-full grid-cols-[1fr_1fr] gap-[6%]">
          <Tiny className="flex rounded-[3px] border border-line bg-surface text-ink-900">
            <span className="inline-flex items-center gap-0.5">
              <Banknote className="size-[1.1em]" />
              Cash
            </span>
          </Tiny>
          <Tiny className="flex rounded-[3px] border border-line bg-surface text-ink-900">
            <span className="inline-flex items-center gap-0.5">
              <CreditCard className="size-[1.1em]" />
              Card
            </span>
          </Tiny>
        </span>
      </R>
    </>
  );
}

// ── A window over the till ──────────────────────────────────────────────────

/** The till behind, greyed, and a window on top: how every dialog looks. */
function Over({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="absolute inset-0 opacity-40 grayscale">
        <TillScreen focus="" />
      </div>
      <div className="absolute inset-0 bg-navy-950/30" />
      <div className="absolute left-[22%] top-[8%] h-[86%] w-[56%] rounded-[6px] bg-surface shadow-pop">
        <Tiny className="h-auto justify-items-start px-[5%] pt-[4%] text-[clamp(7px,2.3cqw,12px)] text-ink-900">
          {title}
        </Tiny>
      </div>
      {children}
    </>
  );
}

/** A button-shaped region inside a window. */
function B({
  tone = "plain",
  children,
}: {
  tone?: "plain" | "brand" | "money";
  children: ReactNode;
}) {
  return (
    <Tiny
      className={cn(
        "rounded-[3px]",
        tone === "plain" && "border border-line bg-surface text-ink-900",
        tone === "brand" && "bg-brand-500 text-white",
        tone === "money" && "bg-money-500 text-white",
      )}
    >
      {children}
    </Tiny>
  );
}

function Payment({ focus }: { focus: string }) {
  return (
    <Over title="Take payment">
      <R id="methods" focus={focus} box={[25, 18, 50, 12]}>
        <span className="grid h-full grid-cols-[repeat(4,1fr)] gap-[4%]">
          <B tone="brand">Cash</B>
          <B>Card</B>
          <B>Mobile</B>
          <B>Account</B>
        </span>
      </R>
      <R id="received" focus={focus} box={[25, 35, 50, 12]}>
        <span className="flex h-full items-center rounded-[3px] border-2 border-brand-500 bg-surface px-[3%]">
          <Tiny className="w-auto text-ink-900">R50.00</Tiny>
        </span>
      </R>
      <R id="notes" focus={focus} box={[25, 51, 50, 11]}>
        <span className="grid h-full grid-cols-[repeat(5,1fr)] gap-[3%]">
          {[
            "bg-money-500",
            "bg-warn-700",
            "bg-danger-500",
            "bg-accent-purple",
            "bg-accent-orange",
          ].map((c) => (
            <span key={c} className={cn("rounded-[2px]", c)} />
          ))}
        </span>
      </R>
      <R id="complete" focus={focus} box={[25, 78, 50, 11]}>
        <B tone="money">Complete</B>
      </R>
    </Over>
  );
}

function Receipt({ focus }: { focus: string }) {
  return (
    <Over title="Sale complete">
      <R id="change" focus={focus} box={[25, 18, 50, 20]} className="bg-brand-50">
        <Tiny className="text-[clamp(7px,2.6cqw,14px)] text-brand-700">Change R8.00</Tiny>
      </R>
      <span className="absolute left-[25%] top-[42%] h-[30%] w-[50%]">
        <Lines n={4} />
      </span>
      <span className="absolute left-[25%] top-[78%] grid h-[10%] w-[50%] grid-cols-[1fr_1fr_1fr_1fr] gap-[3%]">
        <B>Print</B>
        <R id="refund" focus={focus} box={[25, 0, 23, 100]}>
          <B>Refund</B>
        </R>
        <span />
        <span className="col-start-3 col-end-5">
          <B tone="brand">Next customer</B>
        </span>
      </span>
    </Over>
  );
}

function Refund({ focus }: { focus: string }) {
  return (
    <Over title="Refund T01-0042">
      <R id="lines" focus={focus} box={[25, 17, 50, 16]} className="border border-line">
        <span className="flex h-full items-center justify-between px-[4%]">
          <span className="h-[18%] w-[40%] rounded-sm bg-ink-400/50" />
          <Tiny className="w-auto text-ink-900">− 1 +</Tiny>
        </span>
      </R>
      <R id="shelf" focus={focus} box={[25, 35, 50, 10]}>
        <span className="grid h-full grid-cols-[1fr_1fr] gap-[4%]">
          <Tiny className="rounded-[3px] bg-money-50 text-money-700">Shelf</Tiny>
          <Tiny className="rounded-[3px] bg-danger-50 text-danger-700">Write off</Tiny>
        </span>
      </R>
      <R id="reason" focus={focus} box={[25, 48, 50, 10]}>
        <span className="block h-full rounded-[3px] border border-line bg-surface" />
      </R>
      <R id="manager" focus={focus} box={[25, 61, 50, 14]} className="bg-warn-50">
        <Tiny className="text-warn-700">Manager code + PIN</Tiny>
      </R>
      <R id="button" focus={focus} box={[25, 80, 50, 10]}>
        <B tone="brand">Refund R12.00</B>
      </R>
    </Over>
  );
}

function Held({ focus }: { focus: string }) {
  return (
    <Over title="Held sales">
      {[18, 32, 46].map((top, i) => (
        <span
          key={top}
          className="absolute left-[25%] h-[11%] w-[50%] rounded-[3px] border border-line"
          style={{ top: `${top}%` }}
        >
          <span className="absolute left-[5%] top-[40%] h-[18%] w-[40%] rounded-sm bg-ink-400/50" />
          {i === 0 ? (
            <R id="resume" focus={focus} box={[68, 15, 28, 70]}>
              <B tone="brand">Resume</B>
            </R>
          ) : (
            <span className="absolute right-[4%] top-[15%] h-[70%] w-[28%]">
              <B tone="brand">Resume</B>
            </span>
          )}
        </span>
      ))}
    </Over>
  );
}

function OpenShift({ focus }: { focus: string }) {
  return (
    <Over title="Open shift">
      <R id="float" focus={focus} box={[25, 25, 50, 14]}>
        <span className="flex h-full items-center rounded-[3px] border-2 border-brand-500 bg-surface px-[3%]">
          <Tiny className="w-auto text-ink-900">R500.00</Tiny>
        </span>
      </R>
      <R id="open" focus={focus} box={[25, 78, 50, 11]}>
        <B tone="brand">Open shift</B>
      </R>
    </Over>
  );
}

function CashUp({ focus }: { focus: string }) {
  return (
    <Over title="Cash up">
      <R id="counts" focus={focus} box={[25, 17, 50, 52]}>
        <span className="grid h-full gap-[5%] p-[3%]">
          {["bg-accent-orange", "bg-accent-purple", "bg-danger-500", "bg-warn-700"].map((c) => (
            <span key={c} className="flex items-center justify-between gap-[5%]">
              <span className={cn("h-full w-[30%] rounded-[2px]", c)} />
              <span className="h-full w-[22%] rounded-[2px] border border-brand-500 bg-surface" />
            </span>
          ))}
        </span>
      </R>
      <R id="close" focus={focus} box={[25, 78, 50, 11]}>
        <B tone="brand">Close shift</B>
      </R>
    </Over>
  );
}

function ShiftClosed({ focus }: { focus: string }) {
  return (
    <Over title="Shift closed">
      <R id="result" focus={focus} box={[25, 22, 50, 20]} className="bg-warn-50">
        <Tiny className="text-[clamp(7px,2.4cqw,13px)] text-warn-700">Short by R20.00</Tiny>
      </R>
      <span className="absolute left-[25%] top-[48%] h-[26%] w-[50%]">
        <Lines n={4} />
      </span>
      {/* Only managers see this button, so only their steps draw it. */}
      {focus === "approve" && (
        <R id="approve" focus={focus} box={[25, 80, 50, 10]}>
          <B tone="brand">Approve the difference</B>
        </R>
      )}
    </Over>
  );
}

// ── Sign-in, and the device's own home screen ───────────────────────────────

function Login({ focus }: { focus: string }) {
  return (
    <>
      <div className="absolute inset-0 bg-navy-900" />
      <span className="absolute left-[7%] top-[30%] h-[6%] w-[35%] rounded-sm bg-white/70" />
      <span className="absolute left-[7%] top-[40%] h-[6%] w-[28%] rounded-sm bg-gold-500/80" />
      <Lines n={3} className="absolute left-[5%] top-[52%] w-[40%] [&>span]:bg-white/30" />
      <div className="absolute left-[55%] top-[6%] h-[88%] w-[38%] rounded-[6px] bg-surface" />
      <R id="tabs" focus={focus} box={[58, 10, 32, 9]}>
        <span className="grid h-full grid-cols-[1fr_1fr] gap-[4%] rounded-[3px] bg-canvas p-[2%]">
          <Tiny className="flex rounded-[2px] bg-brand-500 text-white">
            <span className="inline-flex items-center gap-0.5">
              <KeyRound className="size-[1.1em]" />
              Till
            </span>
          </Tiny>
          <Tiny className="text-ink-500">Back office</Tiny>
        </span>
      </R>
      <R id="code" focus={focus} box={[58, 23, 32, 9]}>
        <span className="flex h-full items-center rounded-[3px] border-2 border-brand-500 bg-surface px-[4%]">
          <Tiny className="w-auto text-ink-900">NOMSA</Tiny>
        </span>
      </R>
      <R id="pin" focus={focus} box={[60, 36, 28, 40]}>
        <span className="grid h-full grid-cols-[repeat(3,1fr)] gap-[6%] p-[2%]">
          {Array.from({ length: 12 }, (_, i) => (
            <span
              key={i}
              className={cn(
                "rounded-[2px]",
                i === 9 || i === 11 ? "" : "border border-line bg-canvas",
              )}
            />
          ))}
        </span>
      </R>
      <R id="open" focus={focus} box={[58, 81, 32, 9]}>
        <B tone="brand">Open POS</B>
      </R>
    </>
  );
}

function Home({ focus }: { focus: string }) {
  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-br from-navy-800 to-brand-700" />
      <div className="absolute inset-[10%] grid grid-cols-[repeat(6,1fr)] grid-rows-[repeat(3,1fr)] place-items-center gap-[6%]">
        {Array.from({ length: 18 }, (_, i) =>
          i === 7 ? (
            <span key={i} className="relative size-full">
              <R id="icon" focus={focus} box={[10, 10, 80, 80]} className="rounded-[18%]">
                <img src="/icons/icon-192.png" alt="" className="size-full" />
              </R>
            </span>
          ) : (
            <span key={i} className="aspect-square w-[70%] rounded-[22%] bg-white/25" />
          ),
        )}
      </div>
    </>
  );
}

// ── The back office ─────────────────────────────────────────────────────────

const TONE_CLASS: Record<Tone, string> = {
  brand: "bg-brand-500 text-white",
  money: "bg-money-500 text-white",
  plain: "border border-line bg-surface text-ink-900",
  danger: "border border-danger-500/40 bg-surface text-danger-700",
};

function Office({ page, focus }: { page: OfficePage; focus: string }) {
  const L = 16;
  const W = 82;
  let y = 12;
  const parts: ReactNode[] = [];

  if (page.tabs) {
    const width = W / page.tabs.length;
    page.tabs.forEach((tab, i) =>
      parts.push(
        <R
          key={`tab-${tab}`}
          id={`tab:${tab}`}
          focus={focus}
          box={[L + i * width, y, width - 1, 6]}
        >
          <Tiny
            className={cn(
              "rounded-[3px] text-[clamp(4px,1.35cqw,8px)]",
              tab === page.tab ? "bg-surface text-ink-900 shadow-card" : "text-ink-500",
            )}
          >
            {tab}
          </Tiny>
        </R>,
      ),
    );
    y += 9;
  }

  if (page.actions) {
    let right = L + W;
    [...page.actions].reverse().forEach((action) => {
      const width = Math.max(12, action.label.length * 1.45 + 5);
      right -= width;
      parts.push(
        <R key={action.id} id={`action:${action.id}`} focus={focus} box={[right, y, width, 6.5]}>
          <Tiny className={cn("rounded-[3px] px-1", TONE_CLASS[action.tone ?? "plain"])}>
            {action.label}
          </Tiny>
        </R>,
      );
      right -= 1.5;
    });
    y += 9;
  }

  if (page.kpis) {
    const width = W / page.kpis;
    parts.push(
      <R key="kpis" id="kpis" focus={focus} box={[L, y, W, 11]}>
        <span className="flex h-full gap-[1.5%]">
          {Array.from({ length: page.kpis }, (_, i) => (
            <span
              key={i}
              className="flex-1 rounded-[3px] border border-line bg-surface p-[1.5%]"
              style={{ width: `${width}%` }}
            >
              <span className="block h-[22%] w-[50%] rounded-sm bg-ink-400/40" />
              <span className="mt-[8%] block h-[30%] w-[70%] rounded-sm bg-ink-900/60" />
            </span>
          ))}
        </span>
      </R>,
    );
    y += 14;
  }

  if (page.filters) {
    const width = W / page.filters.length;
    page.filters.forEach((label, i) =>
      parts.push(
        <R
          key={`filter-${label}`}
          id={`filter:${label}`}
          focus={focus}
          box={[L + i * width, y, width - 1.5, 6]}
        >
          <Tiny className="justify-items-start rounded-[3px] border border-line bg-surface px-[6%] text-ink-600">
            {label}
          </Tiny>
        </R>,
      ),
    );
    y += 9;
  }

  const bottom = page.panel ? 76 : 97;
  if (page.rows) {
    const rowH = Math.min(8, (bottom - y - 5) / page.rows);
    parts.push(
      <div
        key="table"
        className="absolute rounded-[4px] border border-line bg-surface"
        style={place([L, y, W, 5 + rowH * page.rows])}
      >
        <span className="block h-[5px] border-b border-line" />
      </div>,
    );
    parts.push(
      <R key="rows" id="rows" focus={focus} box={[L, y + 5, W, rowH * page.rows]}>
        <span className="grid h-full" style={{ gridTemplateRows: `repeat(${page.rows}, 1fr)` }}>
          {Array.from({ length: page.rows }, (_, i) => (
            <span
              key={i}
              className="flex items-center gap-[3%] border-b border-line px-[2%] last:border-0"
            >
              <span className="h-[22%] w-[25%] rounded-sm bg-ink-900/50" />
              <span className="h-[22%] w-[15%] rounded-sm bg-ink-400/40" />
              <span className="h-[22%] w-[12%] rounded-sm bg-ink-400/40" />
            </span>
          ))}
        </span>
      </R>,
    );
    if (page.rowAction) {
      const width = Math.max(8, page.rowAction.label.length * 1.45 + 4);
      parts.push(
        <R
          key="row-action"
          id="row-action"
          focus={focus}
          box={[L + W - width - 1, y + 5 + rowH * 0.15, width, rowH * 0.7]}
        >
          <Tiny className={cn("rounded-[3px] px-1", TONE_CLASS[page.rowAction.tone ?? "plain"])}>
            {page.rowAction.label}
          </Tiny>
        </R>,
      );
    }
  }

  if (page.panel) {
    parts.push(
      <R
        key="panel"
        id="panel"
        focus={focus}
        box={[L, 79, W, 18]}
        className="border border-line bg-surface"
      >
        <Tiny className="h-auto justify-items-start px-[2%] pt-[2%] text-ink-900">
          {page.panel}
        </Tiny>
        <Lines n={2} />
      </R>,
    );
  }

  return (
    <>
      {/* The menu down the left, with the page's own item lit. */}
      <div className="absolute bottom-0 left-0 top-0 w-[14%] bg-navy-900" />
      <span className="absolute left-[2%] top-[2.5%] h-[4%] w-[10%] rounded-sm bg-white/80" />
      {NAV.map((item, i) => (
        <R
          key={item}
          id={item === page.nav ? "nav" : `nav:${item}`}
          focus={focus}
          box={[1, 10 + i * 7.5, 12, 6]}
        >
          <Tiny
            className={cn(
              "justify-items-start rounded-[3px] px-[8%] text-[clamp(4px,1.3cqw,8px)]",
              item === page.nav ? "bg-brand-500 text-white" : "text-white/60",
            )}
          >
            {item}
          </Tiny>
        </R>
      ))}

      {/* The top bar, with the page's title. */}
      <div className="absolute left-[14%] right-0 top-0 h-[9%] border-b border-line bg-surface" />
      <Tiny className="absolute left-[16%] top-[2%] h-[5%] w-auto justify-items-start text-[clamp(7px,2.4cqw,13px)] text-ink-900">
        {page.title}
      </Tiny>
      <span className="absolute right-[3%] top-[2%] size-[5%] rounded-full bg-ink-400/40" />

      {parts}
    </>
  );
}

function Form({ where }: { where: FormWhere }) {
  const { fields, buttons, focus } = where;
  const space = 64;
  const fieldH = Math.min(13, space / Math.max(fields.length, 1));
  return (
    <>
      <div className="absolute inset-0 opacity-40 grayscale">
        {where.over === "till" ? <TillScreen focus="" /> : <Office page={where.over} focus="" />}
      </div>
      <div className="absolute inset-0 bg-navy-950/30" />
      <div className="absolute left-[24%] top-[6%] h-[88%] w-[52%] rounded-[6px] bg-surface shadow-pop">
        <Tiny className="h-auto justify-items-start px-[5%] pt-[4%] text-[clamp(7px,2.3cqw,12px)] text-ink-900">
          {where.title}
        </Tiny>
      </div>
      {fields.map((field, i) => {
        const top = 17 + i * fieldH;
        return (
          <span key={field.id}>
            <span
              className="absolute left-[27%] text-[clamp(5px,1.6cqw,9px)] font-medium leading-none text-ink-600"
              style={{ top: `${top}%` }}
            >
              {field.label}
            </span>
            <R id={field.id} focus={focus} box={[27, top + 4.5, 46, Math.max(5, fieldH - 6.5)]}>
              {field.kind === "toggle" ? (
                <span className="flex h-full items-center gap-[4%]">
                  <span className="relative h-[70%] w-[14%] rounded-full bg-brand-500">
                    <span className="absolute right-[8%] top-[12%] aspect-square h-[76%] rounded-full bg-white" />
                  </span>
                </span>
              ) : field.kind === "pin" ? (
                <span className="flex h-full items-center gap-[5%] px-[3%]">
                  {[0, 1, 2, 3].map((d) => (
                    <span key={d} className="aspect-square h-[45%] rounded-full bg-navy-900" />
                  ))}
                </span>
              ) : (
                <span className="flex h-full items-center rounded-[3px] border-2 border-brand-500 bg-surface px-[3%]">
                  <Tiny className="w-auto text-ink-900">{field.value ?? ""}</Tiny>
                </span>
              )}
            </R>
          </span>
        );
      })}
      {buttons.map((button, i) => {
        const width = 46 / buttons.length;
        return (
          <R
            key={button.id}
            id={button.id}
            focus={focus}
            box={[27 + i * width + (i ? 1 : 0), 83, width - 1, 8]}
          >
            <Tiny className={cn("rounded-[3px]", TONE_CLASS[button.tone ?? "plain"])}>
              {button.label}
            </Tiny>
          </R>
        );
      })}
    </>
  );
}

const SCREENS: Record<MapScreen, (props: { focus: string; offline?: boolean }) => ReactNode> = {
  login: Login,
  till: TillScreen,
  payment: Payment,
  receipt: Receipt,
  refund: Refund,
  held: Held,
  "open-shift": OpenShift,
  "cash-up": CashUp,
  "shift-closed": ShiftClosed,
  home: Home,
};

/** The whole screen, with the part the step is about lit up. */
export function TillMap({ where }: { where: Where }) {
  return (
    <div
      aria-hidden
      inert
      className="pointer-events-none relative mx-auto aspect-[16/10] w-full max-w-[26rem] select-none overflow-hidden rounded-[14px] border-[5px] border-navy-900 bg-canvas shadow-raised [container-type:inline-size]"
    >
      {where.screen === "office" ? (
        <Office page={where.page} focus={where.focus} />
      ) : where.screen === "form" ? (
        <Form where={where} />
      ) : (
        <TillScreenFor where={where} />
      )}
    </div>
  );
}

function TillScreenFor({ where }: { where: TillWhere }) {
  const Screen = SCREENS[where.screen];
  return <Screen focus={where.focus} {...(where.offline ? { offline: true } : {})} />;
}
